import { useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { App } from 'antd'
import { createStoryboardCanvas } from '../StoryboardNode/createStoryboardCanvas'

interface UseStoryboardCanvasExportParams {
  storyboardUrls: string[]
  frameCaptions: string[] | undefined
  storyboardTitle: string | undefined
}

export const useStoryboardCanvasExport = ({
  storyboardUrls,
  frameCaptions,
  storyboardTitle,
}: UseStoryboardCanvasExportParams): {
  handleDownloadPNG: () => Promise<void>
  handleCopyToClipboard: () => Promise<void>
} => {
  const { notification: antdNotification } = App.useApp()

  const handleDownloadPNG = useCallback(async () => {
    const canvas = await createStoryboardCanvas(storyboardUrls, frameCaptions)
    if (!canvas) {
      antdNotification.error({
        message: 'Export failed',
        description: 'Could not create storyboard image.',
        title: undefined,
      })
      return
    }

    canvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const filename = (storyboardTitle ?? 'storyboard').replace(/[^a-z0-9]/gi, '_').toLowerCase()
      link.download = `${filename}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      antdNotification.success({
        message: 'Storyboard downloaded',
        description: 'Your storyboard has been saved as a PNG image.',
        title: undefined,
      })
    }, 'image/png')
  }, [storyboardUrls, frameCaptions, storyboardTitle, antdNotification])

  const handleCopyToClipboard = useCallback(async () => {
    const canvas = await createStoryboardCanvas(storyboardUrls, frameCaptions)
    if (!canvas) {
      antdNotification.error({
        message: 'Copy failed',
        description: 'Could not create storyboard image.',
        title: undefined,
      })
      return
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png')
    })

    if (!blob) {
      antdNotification.error({
        message: 'Copy failed',
        description: 'Could not generate image data.',
        title: undefined,
      })
      return
    }

    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
      antdNotification.success({
        message: 'Copied to clipboard',
        description: 'Storyboard image is ready to paste.',
        title: undefined,
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.storyboard', action: 'copy_to_clipboard' },
      })
      antdNotification.error({
        message: 'Copy failed',
        description: 'Your browser may not support copying images.',
        title: undefined,
      })
    }
  }, [storyboardUrls, frameCaptions, antdNotification])

  return { handleDownloadPNG, handleCopyToClipboard }
}
